import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllCategories } from '../services/categoryService';
import { FaChevronDown } from 'react-icons/fa';

const CategoryNavigation = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        // Only fetch active categories for the navigation
        const categoriesData = await getAllCategories(true);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    // Add click outside listener to close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (activeDropdown !== null) {
        const currentRef = dropdownRefs.current[activeDropdown];
        if (currentRef && !currentRef.contains(event.target)) {
          // Check if the click is on a dropdown item
          const isDropdownItem = event.target.closest('.dropdown-item');
          if (!isDropdownItem) {
            setActiveDropdown(null);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const toggleDropdown = (categoryId, e) => {
    // Prevent link from navigating immediately on mobile
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveDropdown(activeDropdown === categoryId ? null : categoryId);
  };
  
  const handleCategoryClick = (url, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(url);
  };

  // Handle subcategory click, ensuring navigation occurs and dropdown closes
  const handleSubcategoryClick = (url, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(null);
    // Use a small timeout to ensure the click is processed before navigation
    setTimeout(() => {
      navigate(url);
    }, 10);
  };

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse h-6 w-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border-b border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-red-500 text-sm text-center">{error}</div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop navigation */}
        <div className="hidden sm:block">
          <div className="flex justify-center space-x-8 py-3">
            {categories
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((category) => (
                <div 
                  key={category.id}
                  className="relative inline-flex items-center"
                  ref={el => dropdownRefs.current[category.id] = el}
                >
                  {category.subcategories && category.subcategories.length > 0 ? (
                    // For categories with subcategories, show dropdown toggle button
                    <button
                      onClick={(e) => toggleDropdown(category.id, e)}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activeDropdown === category.id 
                          ? 'text-white bg-pink-600' 
                          : 'text-gray-700 hover:text-pink-600 hover:bg-pink-50'
                      }`}
                    >
                      {category.name}
                      <FaChevronDown className={`ml-1 h-3 w-3 transition-transform ${
                        activeDropdown === category.id ? 'transform rotate-180' : ''
                      }`} />
                    </button>
                  ) : (
                    // For categories without subcategories, direct link
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-700 hover:text-pink-600 hover:bg-pink-50"
                      onClick={(e) => handleCategoryClick(`/category/${category.slug}`, e)}
                    >
                      {category.name}
                    </Link>
                  )}
                  
                  {/* Dropdown for subcategories */}
                  {activeDropdown === category.id && category.subcategories && category.subcategories.length > 0 && (
                    <div 
                      className="absolute top-full left-0 z-10 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link
                        to={`/category/${category.slug}`}
                        className="block px-4 py-2 text-sm text-pink-700 bg-pink-50 font-medium dropdown-item"
                        onClick={(e) => handleSubcategoryClick(`/category/${category.slug}`, e)}
                      >
                        All {category.name}
                      </Link>
                      {category.subcategories.map((subcategory) => {
                        const name = typeof subcategory === 'string' ? subcategory : subcategory.name;
                        const slug = subcategory.slug || (typeof subcategory === 'string' 
                          ? subcategory.toLowerCase().replace(/\s+/g, '-') 
                          : subcategory.name.toLowerCase().replace(/\s+/g, '-'));
                        const url = `/category/${category.slug}/${slug}`;
                        
                        return (
                          <Link
                            key={subcategory.id || name}
                            to={url}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-700 dropdown-item"
                            onClick={(e) => handleSubcategoryClick(url, e)}
                          >
                            {name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <div className="sm:hidden border-t border-gray-100">
        <div className="px-2 py-2">
          <div className="grid grid-cols-2 gap-2">
            {categories
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((category) => (
                <div 
                  key={category.id} 
                  className="relative"
                  ref={el => dropdownRefs.current[category.id] = el}
                >
                  {category.subcategories && category.subcategories.length > 0 ? (
                    // For categories with subcategories, show dropdown toggle button
                    <button
                      onClick={(e) => toggleDropdown(category.id, e)}
                      className={`w-full flex justify-between items-center px-3 py-2 text-sm font-medium rounded-md ${
                        activeDropdown === category.id 
                          ? 'bg-pink-600 text-white' 
                          : 'bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="truncate">{category.name}</span>
                      <FaChevronDown className={`ml-1 h-3 w-3 transition-transform ${
                        activeDropdown === category.id ? 'transform rotate-180' : ''
                      }`} />
                    </button>
                  ) : (
                    // For categories without subcategories, direct link
                    <Link
                      to={`/category/${category.slug}`}
                      className="w-full flex justify-between items-center px-3 py-2 text-sm font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-pink-50 hover:text-pink-700"
                      onClick={(e) => handleCategoryClick(`/category/${category.slug}`, e)}
                    >
                      <span className="truncate">{category.name}</span>
                    </Link>
                  )}
                  
                  {/* Mobile dropdown for subcategories */}
                  {activeDropdown === category.id && category.subcategories && category.subcategories.length > 0 && (
                    <div 
                      className="mt-1 rounded-md bg-white shadow-sm border border-gray-200 overflow-hidden z-20 absolute w-full"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link
                        to={`/category/${category.slug}`}
                        className="block px-4 py-2 text-sm font-medium text-pink-700 bg-pink-50 border-b border-gray-100 dropdown-item"
                        onClick={(e) => handleSubcategoryClick(`/category/${category.slug}`, e)}
                      >
                        All {category.name}
                      </Link>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {category.subcategories.map((subcategory) => {
                          const name = typeof subcategory === 'string' ? subcategory : subcategory.name;
                          const slug = subcategory.slug || name.toLowerCase().replace(/\s+/g, '-');
                          const url = `/category/${category.slug}/${slug}`;
                          
                          return (
                            <Link
                              key={subcategory.id || name}
                              to={url}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-700 dropdown-item"
                              onClick={(e) => handleSubcategoryClick(url, e)}
                            >
                              {name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default CategoryNavigation; 